import type { SafeToInvest } from '@pfo/domain';
import useSWR from 'swr';

import {
  fetchChecklist,
  fetchClosure,
  fetchReserves,
  fetchSafeToInvest,
  type ChecklistItem,
  type ClosureState,
  type ReserveProgress,
} from '../data/monthlyCloseRepository.js';

export type MonthlyCloseData = Readonly<{
  checklist: readonly ChecklistItem[];
  closure: ClosureState | null;
  /**
   * The month's own surplus minus a risk margin (AC1). Since it is computed for the shown
   * month, a closed month reads its snapshot and the figure stops moving — which is what
   * decision S4 wanted from the separate "Dư tháng" line this replaces.
   */
  safeToInvest: SafeToInvest;
  reserves: readonly ReserveProgress[];
}>;

export type MonthlyCloseState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string; retry: () => void }
  | { status: 'ready'; data: MonthlyCloseData; refresh: () => Promise<unknown> };

/** Everything the "việc hàng tháng" screen shows, for one household, subject and month. */
export function useMonthlyClose(
  householdId: string | null,
  subjectId: string | null,
  year: number,
  month: number,
): MonthlyCloseState {
  const key =
    householdId === null || subjectId === null
      ? null
      : `monthly-close:${householdId}:${subjectId}:${year}-${month}`;

  const { data, error, isLoading, mutate } = useSWR<MonthlyCloseData, Error>(
    key,
    async () => {
      const [checklist, closure, safeToInvest, reserves] = await Promise.all([
        fetchChecklist(householdId!, year, month),
        fetchClosure(year, month),
        fetchSafeToInvest(subjectId!, year, month),
        fetchReserves(subjectId!),
      ]);
      return { checklist, closure, safeToInvest, reserves };
    },
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  if (key === null) return { status: 'idle' };
  if (error) {
    return {
      status: 'error',
      message: error.message || 'Không tải được việc hàng tháng.',
      retry: () => {
        void mutate();
      },
    };
  }
  if (isLoading || data === undefined) return { status: 'loading' };

  return { status: 'ready', data, refresh: () => mutate() };
}
